import { useEffect, useMemo, useState } from 'react';

import { fitWithinBounds, hasIntrinsicSize, ImageSize, NO_IMAGE_SIZE } from './useImageSize.common';

const useImageSize = (uri: string, bounds: ImageSize, intrinsicSize?: ImageSize): ImageSize => {
  const isKnown = hasIntrinsicSize(intrinsicSize);
  const [measuredSize, setMeasuredSize] = useState<ImageSize>(NO_IMAGE_SIZE);

  useEffect(() => {
    // Downloading the whole photo to read `naturalWidth` is the fallback for a
    // photo that arrives without dimensions: a local file picked for upload, and
    // the galleries whose images carry none — message attachments and the sitter
    // relationship gallery.
    if (isKnown || !uri) return;

    const img = new Image();
    img.onload = () => {
      setMeasuredSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = uri;
  }, [isKnown, uri]);

  const { width: boundsWidth, height: boundsHeight } = bounds;

  return useMemo(() => {
    const naturalSize = isKnown && intrinsicSize ? intrinsicSize : measuredSize;
    if (!hasIntrinsicSize(naturalSize)) return NO_IMAGE_SIZE;

    return fitWithinBounds(naturalSize.width, naturalSize.height, {
      width: boundsWidth,
      height: boundsHeight,
    });
  }, [intrinsicSize, isKnown, measuredSize, boundsWidth, boundsHeight]);
};

export default useImageSize;
